import { useChatMessages } from '@ai-sdk-tools/store';
import { useChatStatus } from '@ai-sdk-tools/store';
import { browserLogger } from '@newfront-insurance/dd-rum';
import type { ChatOnFinishCallback } from 'ai';
import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { DurablePreviewProvider, useDurablePreviewState } from '../contexts/durable-preview-context';
import { useNovaChat } from '../hooks/use-nova-chat';
import type { UIMessageWithMetadata } from '../lib/types';

interface ChatRootProps {
  children: ReactNode;
}

/**
 * Root component that sets up the chat context using @ai-sdk-tools/store.
 * This component initializes the chat hook and makes chat state available
 * to all child components via context.
 *
 * Reads chatUuid, initialMessages, and callbacks from context.
 * Syncs hasAnyMessages from AI SDK store to ChatOverlayContext so NovaProvider
 * can use it without needing access to the AI SDK Provider.
 *
 * Stream lifecycle callbacks here deliberately do nothing but refresh the
 * server-backed chats list: unread state and notifications are derived from
 * that data by the notification controller, so a callback firing for an
 * unmounted or switched-away chat is harmless.
 *
 * @example
 * ```tsx
 * <ChatRoot>
 *   <ChatHeader>...</ChatHeader>
 *   <Messages />
 *   <ChatInput />
 * </ChatRoot>
 * ```
 */
export function ChatRoot({ children }: ChatRootProps) {
  const {
    chatUuid,
    initialMessages,
    callbacks,
    setHasAnyMessages,
    notificationController,
    setChatError,
    chatActiveStreamId,
    isLoadingMessages,
    canResumeActiveStream,
    setRetryLastRequest,
  } = useChatOverlayContext();
  const messages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  const previousStatusRef = useRef(status);
  const durablePreview = useDurablePreviewState(chatUuid);
  const { clear: clearDurablePreview, onData: handleDurablePreviewData } = durablePreview;

  const handleFinish: ChatOnFinishCallback<UIMessageWithMetadata> = useCallback(
    async (event) => {
      // A stopped reconnect still reaches AI SDK's onFinish cleanup. It does
      // not represent a completed turn and may belong to a replaced chat view.
      if (event.isAbort) {
        return;
      }

      if (!event.isError) {
        setChatError(undefined);
        setRetryLastRequest(undefined);
      }

      // Surface the turn's outcome (unread flag, cleared stream) promptly;
      // the notification controller evaluates the refreshed list, and counts
      // the turn as seen when this chat is on screen in a focused tab.
      notificationController.noteStreamFinish(chatUuid);

      callbacks?.onFinish?.();
    },
    [callbacks, chatUuid, notificationController, setChatError, setRetryLastRequest],
  );

  // Handle stream errors by surfacing them to the UI via overlay context
  const handleError = useCallback(
    (error: Error) => {
      // User stops, chat replacement, unmount, and an initial 204 reconnect all
      // intentionally abort the response. None should surface as a chat error.
      if (error.name === 'AbortError') {
        return;
      }

      browserLogger.error('[ChatRoot] Stream error', {
        chatUuid,
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
      });

      const isConnectionError =
        error.message.includes('network') ||
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('connection') ||
        error.name === 'AbortError';

      setChatError({
        message: isConnectionError
          ? 'Unable to connect. Please refresh the page or check your network connection.'
          : 'Unable to complete your request. Please try again.',
        timestamp: new Date(),
        type: isConnectionError ? 'network' : 'server',
        canRetry: true,
      });

      callbacks?.onError?.(error);
    },
    [callbacks, chatUuid, setChatError],
  );

  const handleReconnectUnavailable = useCallback(() => {
    // The server is the authority on whether a stream still exists; refresh
    // the list so a cleared activeStreamId stops the spinner.
    notificationController.refetchChats();
  }, [notificationController]);

  // Initialize chat hook - this makes chat state available via context.
  // Resume is held back until the server snapshot has hydrated so a
  // reconnected stream always appends after the restored history.
  useNovaChat({
    chatUuid,
    initialMessages,
    activeStreamId: chatActiveStreamId,
    canResume: !isLoadingMessages && canResumeActiveStream,
    onDurableActiveStreamReconciled: handleReconnectUnavailable,
    onReconnectUnavailable: handleReconnectUnavailable,
    onDurablePreview: handleDurablePreviewData,
    onFinish: handleFinish,
    onError: handleError,
  });

  useEffect(() => {
    setChatError(undefined);
    setRetryLastRequest(undefined);
  }, [chatUuid, setChatError, setRetryLastRequest]);

  // Sync hasAnyMessages from AI SDK store to ChatOverlayContext
  // This ensures NovaProvider always has the current message count
  useEffect(() => {
    setHasAnyMessages(messages.length > 0);
  }, [messages.length, setHasAnyMessages]);

  // Clear any lingering error banner when a new request starts.
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    const wasActive = previousStatus === 'submitted' || previousStatus === 'streaming';
    const isActive = status === 'submitted' || status === 'streaming';

    if (!wasActive && isActive) {
      setChatError(undefined);
    }

    // Commit events normally remove the speculative lane before canonical
    // promotion. Settling the current chat is the fallback for disconnects or
    // malformed streams; lifecycle callbacks may belong to a replaced Chat.
    if (wasActive && !isActive) {
      clearDurablePreview();
    }

    previousStatusRef.current = status;
  }, [clearDurablePreview, setChatError, status]);

  return <DurablePreviewProvider value={durablePreview}>{children}</DurablePreviewProvider>;
}
